import LinkIcon from '@mui/icons-material/Link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';

import { LinkJobButtonProps } from './LinkJobButton.props';
import { ActionPermissions } from '../../../@types';
import { ServiceData } from '../../../@types/service-search.type';
import { isUserHasPermission } from '../../../configs/permissions';
import { useSearchService } from '../../../queries/live-search-query';
import { useUpdateParentService } from '../../../queries/service-query';
import { Button, ButtonSizeProps, ButtonVariantProps } from '../../atoms/Button';
import { LiveSearchBox, LiveSearchListItem } from '../../atoms/LiveSearchBox';
import { SelectMenu } from '../../atoms/SelectMenu';
import { IdSearchItem } from '../../molecules/IdSearchItem';
import { PopupDialog } from '../../molecules/PopupDialog';

type LinkDirection = 'parent' | 'child';

// 'parent' → the searched job is this job's parent (this job becomes its child):
//   PUT /services/parentservice/:id with isChild=false.
// 'child'  → the searched job becomes this job's child: isChild=true.
const DIRECTION_ITEMS = [
  { value: 'parent', label: 'Selected job is the parent of this job' },
  { value: 'child', label: 'Selected job is a child of this job' },
];

/**
 * "Link Job" button + dialog — links this job to another job as its parent or
 * child (parent/child service relationship). Renders nothing unless the user
 * holds the reported-information edit permission that the BFF also enforces.
 */
export const LinkJobButton: React.FC<LinkJobButtonProps> = ({ serviceId, permissions }) => {
  const canLink = isUserHasPermission(
    ActionPermissions.Service_Edit_Service_Parameters_Reported_Information,
    permissions
  );
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number>(NaN);
  const [direction, setDirection] = useState<LinkDirection>('parent');
  const [error, setError] = useState('');

  const searchService = useSearchService();
  const updateParent = useUpdateParentService(serviceId);

  if (!canLink) return null;

  const handleClose = () => {
    setOpen(false);
    setSelectedId(NaN);
    setDirection('parent');
    setError('');
  };

  const handleSave = async () => {
    if (isNaN(selectedId)) {
      setError('Search for and select a job to link.');
      return;
    }
    if (selectedId === serviceId) {
      setError('A job cannot be linked to itself.');
      return;
    }
    try {
      await updateParent.mutateAsync({
        id: serviceId,
        parentServiceId: selectedId,
        isChild: direction === 'child',
      });
      handleClose();
    } catch {
      setError('Could not link the job. Please try again.');
    }
  };

  return (
    <>
      <Button
        variant={ButtonVariantProps.Secondary}
        size={ButtonSizeProps.Small}
        startIcon={<LinkIcon sx={{ fontSize: 16 }} />}
        onClick={() => setOpen(true)}
      >
        Link Job
      </Button>
      <PopupDialog
        title="Link Job"
        isOpen={open}
        width="90%"
        maxWidth="440px"
        actionLabel="Link"
        onClose={handleClose}
        onSave={handleSave}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
            Link this job to another job as its parent or child.
          </Typography>
          <SelectMenu
            id="link-job-direction"
            label="Relationship"
            items={DIRECTION_ITEMS}
            selectedValue={direction}
            onChange={(value: LinkDirection) => setDirection(value)}
          />
          <LiveSearchBox
            title={direction === 'child' ? 'Search for the child job' : 'Search for the parent job'}
            timeOffset={400}
            value={isNaN(selectedId) ? '' : String(selectedId)}
            onClearValue={() => {
              setSelectedId(NaN);
              setError('');
            }}
            onChange={(obj: ServiceData) => {
              setSelectedId(obj.id);
              setError('');
            }}
            onApiInvoke={async (value: string) => await searchService.mutateAsync(value)}
            renderItem={(props, option) => (
              <LiveSearchListItem {...props}>
                <IdSearchItem data={option} />
              </LiveSearchListItem>
            )}
            isError={!!error}
          />
          {error && <Typography sx={{ fontSize: '12px', color: 'error.main' }}>{error}</Typography>}
        </Box>
      </PopupDialog>
    </>
  );
};
